import React, { useContext } from 'react';
import classnames from 'classnames';
import { Url } from '@wix/da-url';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import StatusViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Status';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import PromoBadge from '@wix/da-ds/pkg/Badges/PromoBadge';
import { useSubscriptionTier } from '../../../../utils/hooks/useSubscriptionTier';
import Background from '../../../../Background';
import { DEFAULT_STATUS_PATTERNS } from '../../../../Background/constants';
import TextRenderer from '../../../../TextRenderer';
import { getStatusBackgroundProps } from '../../../_helpers';
import { FeedCardProps } from '../../types';

import legacyTheme from '../../feedcard.legacy.theme.scss';
import s from './ShortStatus.scss';

// note: only short statuses have backgrounds

// TODO: these are custom versions of the backgrounds in Background/constants.ts
// used in the postfeed (w/ a variant for mobile) - look into simplifying.
// Until then and for delete-ability, these are here instead of there.
const POSTFEED_STATUS_PATTERNS = ['meteors', 'hearts', 'stars', 'planets'];
const PATTERN_CDN_PATH = '/eclipse/global/svg/status-backgrounds';

/** Replaces one of the standard Background status patterns with a custom one for the postfeed */
function buildPatternUrl(pattern: string, isMobile?: boolean): string | undefined {
  const index = DEFAULT_STATUS_PATTERNS.indexOf(pattern);
  // e.g. desktop: 'https://st.deviantart.net/eclipse/global/svg/status-backgrounds/meteors.svg',
  // mobile: 'https://st.deviantart.net/eclipse/global/svg/status-backgrounds/meteors-mobile.svg',
  const newPattern = POSTFEED_STATUS_PATTERNS[index];
  const mobileSuffix = isMobile ? '-mobile' : '';
  return newPattern
    ? Url.staticCDNLink(`${PATTERN_CDN_PATH}/${newPattern}${mobileSuffix}.svg`)
    : undefined;
}

const ShortStatusFeedCardContents: React.FC<FeedCardProps> = ({ deviation, width }) => {
  const isMobile = useContext(MobileContext);
  const { tier } = useSubscriptionTier(deviation);

  let backgroundProps: any = getStatusBackgroundProps(deviation);
  const customPattern = buildPatternUrl(backgroundProps?.pattern, isMobile);
  if (customPattern) {
    backgroundProps = { ...backgroundProps, pattern: customPattern };
  }

  return (
    <Background
      className={classnames(s['background'], !!tier && s['in-tier'])}
      contentClassName={s['background-content']}
      width={width}
      variant="status"
      {...backgroundProps}
    >
      {!!tier && <PromoBadge badgeType="tier" size={40} />}

      <TextRenderer
        textContent={deviation.textContent}
        viewerKey={deviation.deviationId}
        legacyTheme="short-status"
        className={classnames(
          legacyTheme['legacy'],
          legacyTheme['override'],
          legacyTheme['short-status']
        )}
        tipTapViewer={StatusViewer}
        cookieType={MeasuredCookieType.EDITOR_STATUS}
        defaultSize={{
          desktop: { width: 780, height: 512 },
          mobile: { width: 380, height: 300 },
        }}
        tipTapPresetId="deviation-status-short"
      />
    </Background>
  );
};
ShortStatusFeedCardContents.displayName = 'ShortStatusFeedCardContents';

export default ShortStatusFeedCardContents;
